import { PastScheduleWarning } from './PastScheduleWarning.tsx';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdsStep } from './AdsStep.tsx';
import { CabinetsStep } from './CabinetsStep.tsx';
import { LaunchSettingsStep } from './LaunchSettingsStep.tsx';
import { useLaunchWizard } from './useLaunchWizard.ts';

// ─── Confirmation modal ───────────────────────────────────────────────────────

function ConfirmLaunchModal({
  wizard,
  onConfirm,
  onCancel,
  launching,
}: {
  wizard: ReturnType<typeof useLaunchWizard>;
  onConfirm: () => void;
  onCancel: () => void;
  launching: boolean;
}) {
  const template = wizard.templates.find((t) => t.id === wizard.templateId);

  // Count total campaigns (rows) across all accounts
  const totalRows = Object.values(wizard.targets).reduce((sum, rows) => sum + rows.length, 0);
  const accountCount = Object.keys(wizard.targets).length;

  // Format scheduledAt for display in Kyiv timezone
  const formatScheduled = (iso: string | null): string => {
    if (!iso) return 'Immediately (Start now)';
    try {
      return new Date(iso).toLocaleString('uk-UA', { timeZone: 'Europe/Kiev', dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const rows = [
    { label: 'Template', value: template?.name ?? '—' },
    { label: 'Accounts', value: `${accountCount}` },
    { label: 'Total campaigns', value: `${totalRows}` },
    { label: 'Ad sets per campaign', value: `${wizard.adsetsCount}` },
    { label: 'Ads per ad set', value: `${wizard.adsPerAdset}` },
    { label: 'Total launches', value: `${totalRows * wizard.adsetsCount * wizard.adsPerAdset}` },
    { label: 'Pause between accounts', value: `${wizard.pauseSeconds}s` },
    { label: 'Start time', value: formatScheduled(wizard.scheduledAt) },
  ];

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Confirm launch</h3>
          <button className="btn btn--ghost btn--sm" onClick={onCancel} disabled={launching}>✕</button>
        </div>

        <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
          Review the launch parameters before confirming.
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
          <tbody>
            {rows.map(({ label, value }) => (
              <tr key={label} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 8px', color: 'var(--text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{label}</td>
                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <PastScheduleWarning dates={Object.values(wizard.targets).flatMap((rows) => rows.map((row) => row.scheduled_at ?? wizard.scheduledAt))} />
        <div className="modal-actions">
          <button className="btn btn--secondary" onClick={onCancel} disabled={launching}>Cancel</button>
          <button className="btn btn--primary" onClick={onConfirm} disabled={launching}>
            {launching ? 'Creating…' : '🚀 Confirm & Launch'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LaunchWizard ─────────────────────────────────────────────────────────────

export function LaunchWizard() {
  const wizard = useLaunchWizard();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [launching, setLaunching] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const next = () => {
    if (step === 1 && !wizard.templateId) return setError('Select a template');
    if (step === 2 && !Object.keys(wizard.targets).length) return setError('Select at least one account');
    setError('');
    setStep(step + 1);
  };

  const handleLaunchClick = () => {
    setError('');
    setShowConfirm(true);
  };

  const confirmLaunch = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      const result = await wizard.submitBatch();
      wizard.reset();
      setShowConfirm(false);
      navigate('/launch-queue', { state: { message: `Batch created: ${result.launches_count} launches queued` } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create batch');
      setShowConfirm(false);
      setLaunching(false);
    }
  };

  // Resolved template name for the header
  const selectedTemplateName = wizard.templates.find((t) => t.id === wizard.templateId)?.name ?? null;

  return (
    <div className="page-body">
      {showConfirm && (
        <ConfirmLaunchModal
          wizard={wizard}
          onConfirm={confirmLaunch}
          onCancel={() => { if (!launching) setShowConfirm(false); }}
          launching={launching}
        />
      )}

      <div className="templates-head">
        <div>
          <h1>
            Launch wizard
            {selectedTemplateName && (
              <span style={{ marginLeft: 12, fontSize: '0.65em', fontWeight: 400, color: 'var(--accent)', background: 'var(--accent-bg, rgba(99,102,241,0.12))', borderRadius: 6, padding: '2px 10px', verticalAlign: 'middle' }}>
                {selectedTemplateName}
              </span>
            )}
          </h1>
          <p className="text-muted text-sm">Prepare campaigns, ad sets, and ads.</p>
        </div>
      </div>

      <div className="wizard-steps">
        {['Settings', 'Accounts', 'Ads'].map((label, i) => (
          <button
            type="button"
            key={label}
            onClick={() => setStep(i + 1)}
            className={step === i + 1 ? 'wizard-stepper wizard-stepper--active' : 'wizard-stepper'}
          >
            <b>{i + 1}</b>{label}
          </button>
        ))}
      </div>

      {error && <div className="notification notification--error">{error}</div>}

      {step === 1 && (
        <LaunchSettingsStep
          templates={wizard.templates}
          templateId={wizard.templateId}
          setTemplateId={wizard.setTemplateId}
          pauseSeconds={wizard.pauseSeconds}
          setPauseSeconds={wizard.setPauseSeconds}
          scheduledAt={wizard.scheduledAt}
          setScheduledAt={wizard.setScheduledAt}
          tzOffsetHours={wizard.tzOffsetHours}
          setTzOffsetHours={wizard.setTzOffsetHours}
        />
      )}

      {step === 2 && (
        <CabinetsStep
          accounts={wizard.adAccounts}
          pages={wizard.pages}
          targets={wizard.targets}
          pixels={wizard.pixels}
          payers={wizard.payers}
          allPayers={wizard.allPayers}
          selectedOrder={wizard.selectedOrder}
          templateBudget={wizard.templateBudget}
          onSelect={(id, yes) => wizard.selectAccount(id, yes, wizard.templateBudget, wizard.scheduledAt)}
          onUpdate={wizard.updateTarget}
          onBulkUpdateAll={wizard.bulkUpdateAllTargets}
          onAddCampaign={wizard.addCampaignRow}
          onSetCampaignCount={wizard.setCampaignCountForAll}
          onRemoveRow={wizard.removeCampaignRow}
          onBulkSelect={(ids) => wizard.bulkSelectAccounts(ids, wizard.templateBudget, wizard.scheduledAt)}
          loadPixels={wizard.loadPixels}
          loadPayers={wizard.loadPayers}
        />
      )}

      {step === 3 && (
        <AdsStep
          targets={wizard.targets}
          accounts={wizard.adAccounts}
          pages={wizard.pages}
          posts={wizard.posts}
          loadPosts={wizard.loadPosts}
          syncPosts={wizard.syncPosts}
          syncingPosts={wizard.syncingPosts}
          postErrors={wizard.postErrors}
          updateTarget={wizard.updateTarget}
          adsetsCount={wizard.adsetsCount}
          setAdsetsCount={wizard.setAdsetsCount}
          adsPerAdset={wizard.adsPerAdset}
          setAdsPerAdset={wizard.setAdsPerAdset}
          selectedOrder={wizard.selectedOrder}
          scheduledAt={wizard.scheduledAt}
          tzOffsetHours={wizard.tzOffsetHours}
        />
      )}

      <div className="template-form__actions">
        {step > 1 && (
          <button className="btn btn--secondary" onClick={() => setStep(step - 1)} disabled={launching}>
            Back
          </button>
        )}
        {step < 3 ? (
          <button className="btn btn--primary" onClick={next}>Next</button>
        ) : (
          <button className="btn btn--primary" onClick={handleLaunchClick} disabled={launching}>
            Launch
          </button>
        )}
      </div>
    </div>
  );
}
